import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Paper,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Restaurant as RestaurantIcon,
  LocalFireDepartment as CalorieIcon,
  FilterList as FilterIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';

const HistoryManagement = ({ onBack }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [foodRecords, setFoodRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMealType, setSelectedMealType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editedRecord, setEditedRecord] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const mealTypes = [
    { value: 'all', label: '全部餐型' },
    { value: 'breakfast', label: '早餐 🌅' },
    { value: 'lunch', label: '午餐 🌞' },
    { value: 'snack', label: '零食 🍪' },
    { value: 'dinner', label: '晚餐 🌙' },
    { value: 'latenight', label: '宵夜 🌃' },
    { value: 'other', label: '其他 🍽️' }
  ];

  useEffect(() => {
    loadFoodRecords();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [foodRecords, selectedDate, selectedMealType, searchQuery]);

  const loadFoodRecords = async () => {
    try {
      setLoading(true);
      const records = await calorieAIService.getAllFoodRecords();
      setFoodRecords(records);
    } catch (error) {
      console.error('載入食物記錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRecords = () => {
    let filtered = [...foodRecords];

    // 按日期篩選
    if (selectedDate) {
      filtered = filtered.filter(record => 
        record.recorded_at.startsWith(selectedDate)
      );
    }

    // 按餐型篩選
    if (selectedMealType !== 'all') {
      filtered = filtered.filter(record => 
        record.meal_type === selectedMealType
      );
    }

    // 按搜尋關鍵字篩選
    if (searchQuery) {
      filtered = filtered.filter(record =>
        record.food_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // 按時間排序（最新的在前）
    filtered.sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));

    setFilteredRecords(filtered);
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setEditedRecord({ ...record });
    setShowEditDialog(true);
  };

  const handleDeleteRecord = (record) => {
    setSelectedRecord(record);
    setShowDeleteDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      await calorieAIService.updateFoodRecord(selectedRecord.id, editedRecord);
      await loadFoodRecords();
      setShowEditDialog(false);
    } catch (error) {
      console.error('更新食物記錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await calorieAIService.deleteFoodRecord(selectedRecord.id);
      await loadFoodRecords();
      setShowDeleteDialog(false);
      setDeleteSuccess(true);
      setTimeout(() => setDeleteSuccess(false), 3000);
    } catch (error) {
      console.error('刪除食物記錄失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMealTypeLabel = (mealType) => {
    const meal = mealTypes.find(m => m.value === mealType);
    return meal ? meal.label : mealType;
  };

  const getMealTypeColor = (mealType) => {
    const colors = {
      breakfast: '#FF9800',
      lunch: '#4CAF50',
      dinner: '#2196F3',
      snack: '#9C27B0',
      latenight: '#673AB7',
      other: '#607D8B'
    };
    return colors[mealType] || '#666';
  };

  const getDateStats = (records) => {
    const totalCalories = records.reduce((sum, record) => sum + record.calories, 0);
    const totalProtein = records.reduce((sum, record) => sum + record.protein, 0);
    const totalCarbs = records.reduce((sum, record) => sum + record.carbs, 0);
    const totalFat = records.reduce((sum, record) => sum + record.fat, 0);
    
    return { totalCalories, totalProtein, totalCarbs, totalFat };
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2, borderRadius: '12px' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="date"
            label="選擇日期"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            select
            label="餐型"
            value={selectedMealType}
            onChange={(e) => setSelectedMealType(e.target.value)}
            size="small"
          >
            {mealTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            placeholder="搜尋食物名稱..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#666' }} />
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderDayView = () => {
    const dayRecords = filteredRecords.filter(record => 
      record.recorded_at.startsWith(selectedDate)
    );
    
    const stats = getDateStats(dayRecords);
    
    // 按餐型分組
    const groupedRecords = dayRecords.reduce((groups, record) => {
      const mealType = record.meal_type;
      if (!groups[mealType]) {
        groups[mealType] = [];
      }
      groups[mealType].push(record);
      return groups;
    }, {});

    return (
      <Box>
        {/* 日期統計 */}
        <Card sx={{ mb: 3, borderRadius: '16px' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1, color: '#FF6B6B' }} />
              {new Date(selectedDate).toLocaleDateString('zh-TW', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                weekday: 'long'
              })}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF6B6B' }}>
                    {Math.round(stats.totalCalories)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    卡路里
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {Math.round(stats.totalProtein)}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    蛋白質
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                    {Math.round(stats.totalCarbs)}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    碳水
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                    {Math.round(stats.totalFat)}g
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    脂肪
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 按餐型顯示記錄 */}
        {Object.entries(groupedRecords).map(([mealType, records]) => (
          <Card key={mealType} sx={{ mb: 2, borderRadius: '12px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <RestaurantIcon sx={{ mr: 1, color: getMealTypeColor(mealType) }} />
                {getMealTypeLabel(mealType)}
                <Chip 
                  label={`${records.length} 個`}
                  size="small"
                  sx={{ ml: 2, backgroundColor: '#f5f5f5' }}
                />
              </Typography>
              
              {records.map((record) => (
                <Box key={record.id} sx={{ mb: 2, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {record.food_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {record.quantity} {record.unit} • {record.completion_percentage || 100}% 食用
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip 
                          label={`${Math.round(record.calories)} 卡`}
                          size="small"
                          sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                        />
                        <Chip 
                          label={`蛋白質 ${Math.round(record.protein)}g`}
                          size="small"
                          sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50' }}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditRecord(record)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteRecord(record)}
                        sx={{ color: '#F44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        ))}

        {dayRecords.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              {selectedDate === new Date().toISOString().split('T')[0] 
                ? '今天還沒有飲食記錄' 
                : '這天沒有飲食記錄'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderListView = () => (
    <Box>
      {filteredRecords.map((record) => (
        <Card key={record.id} sx={{ mb: 2, borderRadius: '12px' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {record.food_name}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {new Date(record.recorded_at).toLocaleDateString('zh-TW')} • {record.completion_percentage || 100}% 食用
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Chip 
                    label={getMealTypeLabel(record.meal_type)}
                    size="small"
                    sx={{ 
                      backgroundColor: getMealTypeColor(record.meal_type),
                      color: 'white'
                    }}
                  />
                  <Chip 
                    label={`${record.quantity} ${record.unit}`}
                    size="small"
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    label={`${Math.round(record.calories)} 卡`}
                    size="small"
                    sx={{ backgroundColor: '#FFE0E0', color: '#FF6B6B' }}
                  />
                  <Chip 
                    label={`蛋白質 ${Math.round(record.protein)}g`}
                    size="small"
                    sx={{ backgroundColor: '#E8F5E8', color: '#4CAF50' }}
                  />
                  <Chip 
                    label={`碳水 ${Math.round(record.carbs)}g`}
                    size="small"
                    sx={{ backgroundColor: '#E3F2FD', color: '#2196F3' }}
                  />
                </Box>
              </Box>
              <Box>
                <IconButton 
                  size="small" 
                  onClick={() => handleEditRecord(record)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={() => handleDeleteRecord(record)}
                  sx={{ color: '#F44336' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {filteredRecords.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary">
            沒有找到符合條件的記錄
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh'}}>
      {/* 頂部導航 */}
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          歷史記錄
        </Typography>
      </Paper>

      {/* 成功提示 */}
      {deleteSuccess && (
        <Alert severity="success" sx={{ m: 2 }}>
          記錄已成功刪除！
        </Alert>
      )}

      {/* 篩選器 */}
      <Box sx={{ p: 2 }}>
        {renderFilters()}

        {/* 標籤頁 */}
        <Paper sx={{ mb: 2, borderRadius: '12px' }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                color: '#666',
                '&.Mui-selected': {
                  color: '#FF6B6B',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#FF6B6B',
              },
            }}
          >
            <Tab label="按日期" />
            <Tab label="列表檢視" />
          </Tabs>
        </Paper>

        {/* 內容區域 */}
        <Box sx={{ pb: 10 }}>
          {currentTab === 0 && renderDayView()}
          {currentTab === 1 && renderListView()}
        </Box>
      </Box>

      {/* 編輯記錄對話框 */}
      <Dialog 
        open={showEditDialog} 
        onClose={() => setShowEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>編輯食物記錄</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="食物名稱"
                  value={editedRecord.food_name || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, food_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="數量"
                  type="number"
                  value={editedRecord.quantity || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, quantity: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="單位"
                  value={editedRecord.unit || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, unit: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="卡路里"
                  type="number"
                  value={editedRecord.calories || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, calories: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  select
                  label="餐型"
                  value={editedRecord.meal_type || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, meal_type: e.target.value })}
                >
                  {mealTypes.slice(1).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="蛋白質 (g)"
                  type="number"
                  value={editedRecord.protein || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, protein: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="碳水 (g)"
                  type="number"
                  value={editedRecord.carbs || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, carbs: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="脂肪 (g)"
                  type="number"
                  value={editedRecord.fat || ''}
                  onChange={(e) => setEditedRecord({ ...editedRecord, fat: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>取消</Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #4CAF50, #66BB6A)' }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除確認對話框 */}
      <Dialog 
        open={showDeleteDialog} 
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除「{selectedRecord?.food_name}」這筆記錄嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>取消</Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            確認刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryManagement;